/* HabitHeatmap — a matrix of habit completions, rows = habits, columns =
   time buckets, filterable week / month / year. Port of the mobile component. */
import React, { useMemo, useState } from 'react';
import { isDueOn, scheduledCountOverRange } from '@habishine/shared';
import { addDaysStr, type DayCheckin, type Habit, toDayStr, todayStr, useWeekStart, weekStartOf, weekdayLabels } from '@habishine/client-core';

import { hueVar } from '../theme/hues';

type Range = 'week' | 'month' | 'year';
const RANGES: { key: Range; label: string }[] = [{ key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }, { key: 'year', label: 'Year' }];

export function Heatmap({ habits, checkins }: { habits: Habit[]; checkins: Record<string, Record<string, DayCheckin>> }) {
  const [range, setRange] = useState<Range>('week');
  const { weekStartsOn } = useWeekStart();
  const today = todayStr();
  const cols = useMemo(() => {
    if (range === 'week') {
      const start = toDayStr(weekStartOf(new Date(), weekStartsOn));
      return Array.from({ length: 7 }, (_, i) => addDaysStr(start, i));
    }
    if (range === 'month') return Array.from({ length: 30 }, (_, i) => addDaysStr(today, i - 29));
    const y = new Date().getFullYear();
    return Array.from({ length: 12 }, (_, m) => `${y}-${String(m + 1).padStart(2, '0')}`);
  }, [range, today, weekStartsOn]);
  const labels = range === 'week' ? weekdayLabels(weekStartsOn) : range === 'year' ? ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'] : [];

  /** 0..1 for a bucket, or null for a rest day (not due) / a day before the habit began. */
  const value = (h: Habit, col: string): number | null => {
    const days = checkins[h.id] ?? {};
    if (range === 'year') {
      const [y, m] = col.split('-').map(Number);
      const first = `${col}-01`;
      const last = toDayStr(new Date(y!, m!, 0));
      if (last < h.startsOn) return null;
      const asked = scheduledCountOverRange(h.schedule, first, last < today ? last : today, h.startsOn, weekStartsOn);
      if (asked <= 0) return null;
      const kept = Object.keys(days).filter((d) => d.startsWith(col) && days[d]?.completed).length;
      return Math.min(1, kept / asked);
    }
    if (col < h.startsOn || col > today) return null;
    if (!isDueOn(h.schedule, col, h.startsOn)) return null;
    const ck = days[col];
    if (!ck) return 0;
    return ck.completed ? 1 : h.target > 1 ? ck.progress / h.target : 0;
  };

  return (
    <div className="surface p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-head text-ink">Completion matrix</h3>
        <div className="flex gap-0.5 rounded-full bg-card-sunk p-1">
          {RANGES.map((r) => (
            <button key={r.key} type="button" onClick={() => setRange(r.key)} className={`rounded-full px-3 py-1 text-[12px] font-semibold transition-all ${range === r.key ? 'bg-card text-ink shadow-[0_1px_2px_rgba(20,20,25,0.08),0_4px_12px_-4px_rgba(20,20,25,0.14)]' : 'text-meta hover:text-ink'}`}>{r.label}</button>
          ))}
        </div>
      </div>
      {labels.length > 0 ? (
        <div className="mb-1 flex items-center gap-2">
          <span className="w-7" />
          <div className="grid flex-1 gap-1" style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` }}>
            {labels.map((l, i) => <span key={i} className="text-center text-[10px] font-semibold text-tertiary">{l}</span>)}
          </div>
        </div>
      ) : null}
      {habits.map((h) => (
        <div key={h.id} className="mb-1.5 flex items-center gap-2">
          <span className="w-7 text-center text-sm" title={h.name}>{h.emoji || '✦'}</span>
          <div className="grid flex-1 gap-1.5" style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` }}>
            {cols.map((col) => {
              // a day that hasn't happened yet is blank, not a rest day
              const future = range !== 'year' && col > today;
              const v = future ? null : value(h, col);
              const bg = v === null ? 'transparent' : v >= 0.75 ? hueVar(h.hue, 'c') : v >= 0.4 ? hueVar(h.hue, 'k') : v >= 0.15 ? hueVar(h.hue, 't') : 'var(--card-sunk)';
              return <span key={col} className="h-[18px] rounded-[6px] transition-transform duration-150 hover:scale-110" style={{ background: bg, border: v === null && !future ? '1px dashed var(--hairline)' : undefined }} title={`${h.name} · ${col}`} />;
            })}
          </div>
        </div>
      ))}
      <p className="mt-2 text-[11px] text-tertiary">Darker = more consistent{range !== 'year' && habits.some((h) => h.schedule.kind === 'weekdays') ? ' · dashed = rest day' : ''}</p>
    </div>
  );
}
